import React, { useCallback, useContext as useContextCore, useState } from 'react'
import type { TableComponents, TableContext, OnSelectFunction, TableOptions, TableProviderProps } from './types'
import merge from 'lodash/merge'
import { TableBody } from './table-body/TableBody'
import { TableFooter } from './table-footer/TableFooter'
import { TableHeader } from './table-header/TableHeader'
import { useTableConfig } from './hooks/useTableConfig'
import { useBuildTableName } from './hooks/useBuildTableName'
import type { ListAction } from '@ztechapps/common.hook'
import { useAllLocalStorage } from '@ztechapps/common.hook'
import isEmpty from 'lodash/isEmpty'

const context = React.createContext({} as TableContext)
const CoreProvider = context.Provider

const defaultTableOptions: TableOptions = {
  responsive: true,
  hover: true,
  striped: true,
  style: {
    width: 'unset',
  },
}
const defaultComponents: TableComponents = {
  tableBody: TableBody,
  tableFoot: TableFooter,
  tableHead: TableHeader,
}

export function TableProvider<T = Record<any, any>>({
  children,
  height,
  dataSuspenseOptions,
  tableOptions,
  initialSelectedItem,
  onSelect,
  name,
  fields,
  components,
  onRowDoubleClick,
  ...props
}: TableProviderProps<T>) {
  //set the default components
  components = merge({}, defaultComponents, components)

  //set the defaults
  dataSuspenseOptions = dataSuspenseOptions ?? {}
  tableOptions = merge({}, defaultTableOptions, tableOptions)
  name = useBuildTableName(name, fields)

  const localStorage = useAllLocalStorage()

  const configStorage = useTableConfig(name, fields, localStorage)

  //we need to set the default for selected item
  const [selectedItem, setSelectedItem] = React.useState(initialSelectedItem)
  const _onSelect: OnSelectFunction<T> = useCallback(
    (item) => {
      setSelectedItem(item)
      if (onSelect) {
        onSelect(item)
      }
    },
    [onSelect]
  )

  const internalOnRowDoubleClick: OnSelectFunction<T> = useCallback(
    (item) => {
      _onSelect(item)

      if (onRowDoubleClick) {
        onRowDoubleClick(item)
      }
    },
    [_onSelect, onRowDoubleClick]
  )

  const [showConfig, setShowConfig] = useState(false)

  const { storage, storageKey } = props.listResult

  const getStoredFilter = useCallback(
    (fieldName: string) => {
      if (!storage) return null
      const storedListAction = storage.get(storageKey, true) as ListAction
      return storedListAction?.tableFilter?.[fieldName] ?? null
    },
    [storage, storageKey]
  )

  const hasFilterValue = useCallback(
    (fieldName: string): boolean => {
      const storedFilter = getStoredFilter(fieldName)
      return !isEmpty(storedFilter)
    },
    [getStoredFilter]
  )

  const hasActiveFilter = useCallback(() => {
    if (!storage) return false
    const storedListAction = storage.get(storageKey, true) as ListAction

    return !isEmpty(storedListAction?.tableFilter)
  }, [storage, storageKey])

  const resetFilter = async () => {
    props?.listResult?.onFilter?.({
      tableFilter: {
        __reset: { value: true },
      },
    })
  }

  return (
    <CoreProvider
      value={{
        ...props,
        selectedItem,
        setSelectedItem,
        height,
        dataSuspenseOptions,
        tableOptions,
        onSelect: _onSelect,
        listFields: configStorage.listFields,
        fields: configStorage.fields,
        setFields: configStorage.setFields,
        onResetConfig: configStorage.onResetConfig,
        name,
        components,
        onRowDoubleClick: internalOnRowDoubleClick,
        showConfig,
        setShowConfig,
        getStoredFilter,
        hasFilterValue,
        hasActiveFilter,
        onResetFilter: resetFilter,
      }}
    >
      {children}
    </CoreProvider>
  )
}

export const useTableContext = () => useContextCore(context)
